import { useCallback, useEffect, useState } from "react";
import { useLang } from "../hooks/useLang";
import { listProjects, createProject, deleteProject, type Project, type ProjectInput } from "../services/projects";
import { listBillingAddresses, type BillingAddress } from "../services/billing";
import Icon from "./icons";

type SubTab = "list" | "add";

const EMPTY: ProjectInput = { name: "", description: "", billing_address_id: null, status: "taslak" };

const STATUS_BADGE: Record<Project["status"], string> = {
  taslak: "off",
  aktif: "ok",
  tamamlandi: "neutral",
};

function fmtDate(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR");
}

export default function ProjectsPanel() {
  const { t } = useLang();
  const [items, setItems] = useState<Project[]>([]);
  const [billing, setBilling] = useState<BillingAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sub, setSub] = useState<SubTab>("list");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ProjectInput>(EMPTY);

  const set = <K extends keyof ProjectInput>(k: K, v: ProjectInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [p, b] = await Promise.all([listProjects(), listBillingAddresses()]);
      setItems(p);
      setBilling(b);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onAdd = useCallback(async () => {
    if (!form.name.trim()) { setErr(t("proj_name_required")); return; }
    setCreating(true);
    setErr(null);
    try {
      await createProject(form);
      setForm(EMPTY);
      await load();
      setSub("list"); // kayıt sonrası listeye dön
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }, [form, load, t]);

  const onDelete = useCallback(async (p: Project) => {
    if (!window.confirm(t("proj_delete_confirm").replace("{name}", p.name))) return;
    setErr(null);
    try {
      await deleteProject(p.id);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [load, t]);

  const statusLabel = (s: Project["status"]) =>
    s === "taslak" ? t("proj_status_taslak") : s === "aktif" ? t("proj_status_aktif") : t("proj_status_tamamlandi");

  return (
    <>
      <p className="hint">{t("projects_intro")}</p>

      {err && <div className="error">{t("err_prefix")}: {err}</div>}

      {/*
        Alt sekmeler — BillingPanel ile aynı gerekçe: ekleme formu uzun olduğu
        için liste sayfanın çok aşağısında kalıyordu (müşteri bulgusu
        2026-07-30). Varsayılan liste.
      */}
      <div className="settings-tabs subtabs" role="tablist">
        <button
          type="button" role="tab" aria-selected={sub === "list"}
          className={"settings-tab" + (sub === "list" ? " is-active" : "")}
          onClick={() => setSub("list")}
        >
          <Icon name="folder" size={15} /> {t("projects_list_title")}
        </button>
        <button
          type="button" role="tab" aria-selected={sub === "add"}
          className={"settings-tab" + (sub === "add" ? " is-active" : "")}
          onClick={() => setSub("add")}
        >
          <Icon name="plus" size={15} /> {t("proj_add_title")}
        </button>
      </div>

      {sub === "add" && <section className="settings-section">
        <h3>{t("proj_add_title")}</h3>

        <label className="form-row">
          <span>{t("proj_name_label")}</span>
          <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("proj_name_ph")} />
        </label>

        <label className="form-row">
          <span>{t("proj_desc_label")}</span>
          <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("proj_desc_ph")} />
        </label>

        <label className="form-row">
          <span>{t("proj_billing_label")}</span>
          <select
            value={form.billing_address_id ?? ""}
            onChange={(e) => set("billing_address_id", e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t("proj_billing_none")}</option>
            {billing.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
          </select>
        </label>

        <label className="form-row">
          <span>{t("proj_status_label")}</span>
          <select value={form.status} onChange={(e) => set("status", e.target.value as Project["status"])}>
            <option value="taslak">{t("proj_status_taslak")}</option>
            <option value="aktif">{t("proj_status_aktif")}</option>
            <option value="tamamlandi">{t("proj_status_tamamlandi")}</option>
          </select>
        </label>

        <div className="settings-actions">
          <button className="btn-pri" onClick={onAdd} disabled={creating || !form.name.trim()}>
            {creating ? t("saving") : t("proj_add_btn")}
          </button>
        </div>
      </section>}

      {sub === "list" && <section className="settings-section">
        <h3>{t("projects_list_title")}</h3>
        {loading && <div className="hint">{t("projects_loading")}</div>}
        {!loading && items.length === 0 && <div className="hint">{t("projects_empty")}</div>}

        <div style={{ display: "grid", gap: 8 }}>
          {items.map((p) => (
            <div key={p.id} style={{
              display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
              padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 6,
            }}>
              <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {p.name} <span className={"badge " + STATUS_BADGE[p.status]}>{statusLabel(p.status)}</span>
                </div>
                {p.description && <div className="hint" style={{ fontSize: 12.5 }}>{p.description}</div>}
                <div className="hint" style={{ fontSize: 12 }}>
                  {p.billing_title ? `${t("proj_col_billing")}: ${p.billing_title} · ` : ""}{fmtDate(p.created_at)}
                </div>
              </div>
              <button className="btn-sec" onClick={() => onDelete(p)}>{t("delete")}</button>
            </div>
          ))}
        </div>
      </section>}
    </>
  );
}
